const MANIFEST_STORAGE_KEY = 'cs_recording_recovery_v1';
import {
  advanceRecordingManifest,
  createRecordingManifest,
  transitionRecordingManifest
} from './recording-session-manifest.js';

function readManifests(manifestStorage) {
  try {
    const stored = manifestStorage?.getItem(MANIFEST_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function persistManifests(manifestStorage, manifests) {
  manifestStorage?.setItem(MANIFEST_STORAGE_KEY, JSON.stringify(manifests));
}

function extensionFor(mimeType) {
  return mimeType?.includes('mp4') ? 'mp4' : 'webm';
}

function recoveryFileName(recordingId, mimeType) {
  const safeId = String(recordingId).replace(/[^a-zA-Z0-9_-]/g, '_');
  return `recording-${safeId}.${extensionFor(mimeType)}`;
}

function isMissingFile(error) {
  return error?.name === 'NotFoundError';
}

export function createRecordingRecoveryRepository({ storageRoot, manifestStorage, now = Date.now }) {
  if (!storageRoot) throw new TypeError('storageRoot is required');

  const queues = new Map();

  function getManifest(recordingId) {
    return readManifests(manifestStorage)[recordingId] || null;
  }

  function saveManifest(manifest) {
    const manifests = readManifests(manifestStorage);
    manifests[manifest.recordingId] = manifest;
    persistManifests(manifestStorage, manifests);
    return manifest;
  }

  function deleteManifest(recordingId) {
    const manifests = readManifests(manifestStorage);
    delete manifests[recordingId];
    if (Object.keys(manifests).length === 0) {
      manifestStorage?.removeItem(MANIFEST_STORAGE_KEY);
    } else {
      persistManifests(manifestStorage, manifests);
    }
  }

  function enqueue(recordingId, operation) {
    const previous = queues.get(recordingId) || Promise.resolve();
    const current = previous.then(operation);
    const guarded = current.catch(() => {}).finally(() => {
      if (queues.get(recordingId) === guarded) queues.delete(recordingId);
    });
    queues.set(recordingId, guarded);
    return current;
  }

  async function begin(metadata) {
    if (!metadata?.recordingId) throw new TypeError('recordingId is required');

    const fileName = metadata.fileName || recoveryFileName(metadata.recordingId, metadata.mimeType);
    const fileHandle = await storageRoot.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(new Blob([]));
    await writable.close();

    return saveManifest(createRecordingManifest({
      ...metadata,
      fileName
    }, now()));
  }

  function progress(recordingId, chunkBytes, healthEvidence = null) {
    return enqueue(recordingId, async () => {
      const manifest = getManifest(recordingId);
      if (!manifest) throw new Error(`Unknown recording: ${recordingId}`);
      return saveManifest(advanceRecordingManifest(manifest, {
        chunkBytes,
        healthEvidence,
        at: now()
      }));
    });
  }

  function write(recordingId, chunk) {
    return enqueue(recordingId, async () => {
      const manifest = getManifest(recordingId);
      if (!manifest) throw new Error(`Unknown recording: ${recordingId}`);
      if (!(chunk instanceof Blob)) throw new TypeError('chunk must be a Blob');
      if (manifest.state !== 'recording') {
        throw new Error(`Recording ${recordingId} is already finalizing or not recording`);
      }

      let writable;
      try {
        const fileHandle = await storageRoot.getFileHandle(manifest.fileName);
        const existing = await fileHandle.getFile();
        writable = await fileHandle.createWritable({ keepExistingData: true });
        await writable.seek(existing.size);
        await writable.write(chunk);
        await writable.close();

        return saveManifest(advanceRecordingManifest(manifest, {
          chunkBytes: chunk.size,
          at: now()
        }));
      } catch (error) {
        try {
          await writable?.abort?.();
        } catch {
          // Preserve the original write error.
        }
        saveManifest(transitionRecordingManifest(manifest, 'error'));
        throw error;
      }
    });
  }

  function remove(recordingId) {
    return enqueue(recordingId, async () => {
      const manifest = getManifest(recordingId);
      if (!manifest) return;

      try {
        await storageRoot.removeEntry(manifest.fileName);
      } catch (error) {
        if (!isMissingFile(error)) throw error;
      }
      deleteManifest(recordingId);
    });
  }

  function finalize(recordingId) {
    return enqueue(recordingId, async () => {
      const manifest = getManifest(recordingId);
      if (!manifest) throw new Error(`Unknown recording: ${recordingId}`);

      saveManifest(transitionRecordingManifest(manifest, 'finalizing'));
      try {
        const fileHandle = await storageRoot.getFileHandle(manifest.fileName);
        const file = await fileHandle.getFile();
        if (file.size === 0) throw new Error('Cannot finalize a zero-byte recording');

        const readyManifest = saveManifest(transitionRecordingManifest(
          manifest,
          'ready',
          { bytes: file.size }
        ));
        return {
          blob: new Blob([file], { type: manifest.mimeType }),
          manifest: readyManifest,
          cleanup: () => remove(recordingId)
        };
      } catch (error) {
        saveManifest(transitionRecordingManifest(manifest, 'error'));
        throw error;
      }
    });
  }

  async function listRecoverable() {
    const recordingIds = Object.keys(readManifests(manifestStorage));
    const recoverable = [];

    for (const recordingId of recordingIds) {
      await (queues.get(recordingId) || Promise.resolve());
      const manifest = getManifest(recordingId);
      if (!manifest) continue;
      try {
        const fileHandle = await storageRoot.getFileHandle(manifest.fileName);
        const file = await fileHandle.getFile();
        if (file.size > 0) recoverable.push({ ...manifest, bytes: file.size });
      } catch (error) {
        if (!isMissingFile(error)) throw error;
        deleteManifest(recordingId);
      }
    }

    return recoverable.sort((left, right) => left.createdAt - right.createdAt);
  }

  async function confirmSaved(recordingId) {
    const finished = await finalize(recordingId);
    await finished.cleanup();
  }

  return { begin, progress, write, finalize, listRecoverable, confirmSaved, remove };
}
